import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ThemeService } from '@shared/services/theme.service';
import { MapTransformService } from '@shared/shared/modules/map/services/map-transform.service';
import { Location } from '@shared/shared/services/location-service';
import { isMobile } from '@shared/utils/platform';
import { Feature, Point } from 'geojson';
import * as mapboxgl from 'mapbox-gl';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { Observable, of, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { MapPosition, PopupGeoJsonProperties } from '../../models';
import { MapDirectionsService } from '../../services/map-directions.service';
import { MapState, SaveMapPositionAction, selectMapPosition } from '../../store';
import { isGeoJSONFeatureCollection } from '../../utils';

@Component({
  selector: 'ten-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, MatIconModule, TranslateModule, NgxMapboxGLModule],
})
export class MapComponent implements OnInit, OnChanges, OnDestroy {
  private lastPinList: number[];

  /**
   * Name for the default custom pin icon.
   */
  private static ICON_NAME = 'ten-map-pin';

  /**
   * Path to the default custom SVG icon.
   * Note: this must be an SVG file path.
   */
  private static ICON_PATH = 'assets/images/map/pin.svg';

  /**
   * A list of geometry objects of type Point.
   */
  @Input()
  public geoJSON:
    | GeoJSON.Feature<GeoJSON.Point, PopupGeoJsonProperties>
    | GeoJSON.FeatureCollection<GeoJSON.Point, PopupGeoJsonProperties>;

  /**
   * Currently selected GeoJSON.Feature item ID.
   * This can be used in many ways, e.g.
   * to display a popup for that feature or
   * make the marker bigger.
   */
  @Input()
  public activeFeatureId: number | string;

  /**
   * Identifier of the map, the map state will be saved and retrieved by using this id.
   * It allows to have multiple maps with state-restoration mechanism.
   * If not set, the state won't be saved or retrieved.
   */
  @Input()
  public mapId: string;

  /** */
  @Input()
  public closePopupOnClick: boolean;

  @Input()
  public disableZoom = false;

  @Input()
  public hidePopupText = false;

  @Input()
  public zoomToActivePin = true;

  @Input()
  public enableDirectionsToPin = false;

  @Input()
  public overlayText: string;

  @Input()
  public isRecenterButton = false;

  @Input()
  public searchOnDrag = false;

  @Input()
  public geo: Location;

  /**
   * Stream emitting a GeoJSON.Feature when user
   * hovers on any layer symbol.
   */
  @Output()
  public featureHovered = new EventEmitter<mapboxgl.MapboxGeoJSONFeature | undefined>();

  /**
   * Stream emitting a GeoJSON.Feature when user
   * clicks on any layer symbol.
   */
  @Output()
  public featureClicked = new EventEmitter<mapboxgl.MapboxGeoJSONFeature>();

  @Output()
  public pinList = new EventEmitter<number[]>();

  @Output()
  public zoomIn = new EventEmitter<mapboxgl.MapboxGeoJSONFeature>();

  @Output()
  public zoomOut = new EventEmitter<mapboxgl.MapboxGeoJSONFeature>();

  @Output()
  public mapMoved = new EventEmitter<{ geolocation: mapboxgl.LngLat; maxDistance: number }>();

  // Icon from material icons
  public svgIconAsElement: SVGElement;

  /**
   * Coordinates to let Mapbox know
   * how to center the map.
   */
  public center: mapboxgl.LngLatLike;

  /**
   * Control mapbox.Map initial zoom setting.
   */
  public zoom: number[] = [13];

  /**
   * If set, the map will center on the given coordinates.
   * @see https://tools.ietf.org/html/rfc7946#section-5
   */
  public bounds: mapboxgl.LngLatBounds;

  /**
   * Options that will affect centering
   * the map to boundaries contained in LngLatBounds,
   * passed through to the MapComponent.bounds property.
   */
  public boundsOptions: mapboxgl.FitBoundsOptions = {
    padding: 40,
    maxZoom: 13,
  };

  /**
   * Control the style of the cursor
   * while hovering over a geometry object.
   */
  public cursorStyle: string;

  public popup: mapboxgl.Popup;

  private currentPins: Array<any> = [];
  private mapboxMap: mapboxgl.Map;
  private pinDirectionLoaded = false;
  public overlayTextActive = true;
  public firstWheel = false;
  public firstDrag = false;
  private initZoom = 0;
  private hoveredStateId: string | number;

  private onMouseLeavePointsHandler;
  private onMouseMoveOverPointsHandler;
  private onClickPointsHandler;
  private onMoveEndHandler;
  private onZoomStartHandler;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private iconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private mapTransformationService: MapTransformService,
    private mapDirectionsService: MapDirectionsService,
    private themeService: ThemeService,
    private ngrxStore: Store<MapState>
  ) {
    this.onMouseLeavePointsHandler = this.onMouseLeavePoints.bind(this);
    this.onMouseMoveOverPointsHandler = this.onMouseMoveOverPoints.bind(this);
    this.onClickPointsHandler = this.onClickPoints.bind(this);
    this.onMoveEndHandler = this.onMoveEnd.bind(this);
    this.onZoomStartHandler = this.onZoomStart.bind(this);
  }

  public ngOnInit() {
    this.iconRegistry.getNamedSvgIcon(MapComponent.ICON_NAME).subscribe((svg) => {
      this.svgIconAsElement = svg;
    });

    this.themeService.loadStyle('mapbox');
  }

  public closeMapInfo() {
    this.overlayTextActive = false;
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.setSvgIcons();

    const geoJSONChange = changes['geoJSON'];
    const geolocationChange = changes['geo'];

    if (geolocationChange && geolocationChange.currentValue) {
      this.updateBoundingBox(false);
      return;
    }

    if (geoJSONChange && geoJSONChange.firstChange === true) {
      if (isGeoJSONFeatureCollection(this.geoJSON)) {
        this.geoJSON.features.map((value) => {
          value.geometry.coordinates = this.transformCoordinates(value.geometry.coordinates);
        });
      }

      this.setMapCenter();

      if (this.mapId) {
        this.getSavedMapPosition().subscribe((position) => {
          if (position && position.pins) {
            this.emitDistinctPinList(position.pins);
          }
        });
      }
    }

    // The geoJSON can get changed
    // when this component is used to
    // show paginated sets of locations.
    // When that happens, you need to help
    // the map with animating into a right place.
    if (geoJSONChange && geoJSONChange.firstChange === false) {
      // ensures the pins are removed from the map and then rerendered
      this.currentPins.forEach((item) => {
        item.remove();
      });
      this.currentPins = [];

      this.setUpCustomMapboxIcon(this.mapboxMap);

      if (!this.searchOnDrag) {
        if (!isGeoJSONFeatureCollection(this.geoJSON)) {
          const currentPosition = geoJSONChange.currentValue.geometry.coordinates;
          this.geoJSON.geometry.coordinates = this.transformCoordinates(currentPosition);
        }
        this.updateBoundingBox(false);
      }
    }

    // In case user moved the map view
    // around the world, bring all the
    // GeoJSON features back in the map view
    // when selected feature gets updated.
    const activeFeatureIdChange = changes['activeFeatureId'];
    if (activeFeatureIdChange) {
      if (this.zoomToActivePin) {
        this.updateBoundingBox();
      }

      // Pin image change
      if (this.mapboxMap) {
        this.mapboxMap.setLayoutProperty('points', 'icon-image', [
          'match',
          ['id'],
          Number(activeFeatureIdChange.currentValue),
          'marker-selected',
          'marker',
        ]);

        // Popup change
        this.showPopup(this.getActiveFeature(activeFeatureIdChange.currentValue));
      }
    }
  }

  public ngOnDestroy(): void {
    if (!this.mapboxMap) {
      return;
    }

    this.mapboxMap.off('mousemove', 'points', this.onMouseMoveOverPointsHandler);
    this.mapboxMap.off('mouseleave', 'points', this.onMouseLeavePointsHandler);
    this.mapboxMap.off('click', 'points', this.onClickPointsHandler);
    this.mapboxMap.off('moveend', this.onMoveEndHandler);
    this.mapboxMap.off('zoomstart', this.onZoomStartHandler);
  }

  /**
   * Array comes in as lng, lat
   * @param coordinates
   */
  public transformCoordinates(coordinates: Array<number>): Array<number> {
    // this function wants lat, lng so we have to swp around
    const transformed = this.mapTransformationService.gcj2wgs(coordinates[1], coordinates[0]);
    return [transformed.lng, transformed.lat];
  }

  private setMapLanguage() {
    const availableLanguages = ['ar', 'zh', 'en', 'fr', 'de', 'it', 'ja', 'ko', 'pt', 'ru', 'es'];

    const getLanguage = () => {
      //always set when site selected
      const languageCode = localStorage?.getItem('ten-language-settings') ?? 'en';
      const primaryLanguagePart = languageCode.split('-')[0];

      return availableLanguages.includes(primaryLanguagePart) ? `name_${primaryLanguagePart}` : 'name_en';
    };

    const excludedLayerPatterns = ['highway', 'motorway', 'road-shields-white', 'road-shields-black'];
    const mapTranslatedLanguage = getLanguage();
    this.mapboxMap.getStyle().layers.forEach((layer) => {
      if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
        const isExcluded = excludedLayerPatterns.some((pattern) => layer.id.includes(pattern));
        if (isExcluded) {
          return;
        }

        this.mapboxMap.setLayoutProperty(layer.id, 'text-field', [
          'format',
          ['get', mapTranslatedLanguage],
          { 'font-scale': 1.0 },
        ]);
      }
    });
  }

  /**
   * Handle the mapboxgl.Map "load" event.
   */
  public onMapLoad(mapboxMap: mapboxgl.Map) {
    this.mapboxMap = mapboxMap;

    if (isMobile()) {
      this.addUserLocationPin();
    }

    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: this.closePopupOnClick,
    });
    this.setUpCustomMapboxIcon(mapboxMap);
    if (this.activeFeatureId) {
      this.showPopup(this.getActiveFeature(this.activeFeatureId));
    }

    this.setMapLanguage();

    this.mapboxMap.once('idle', () => {
      this.mapboxMap.on('moveend', this.onMoveEndHandler);
      this.mapboxMap.on('mousemove', 'points', this.onMouseMoveOverPointsHandler);
      this.mapboxMap.on('mouseleave', 'points', this.onMouseLeavePointsHandler);
      this.mapboxMap.on('click', 'points', this.onClickPointsHandler);
      this.mapboxMap.on('zoomstart', this.onZoomStartHandler);
    });

    // Ensure all points are in the visible area.
    // The reason this is also triggered here
    // apart from the #ngOnChanges hook is that
    // user gets a chance to see the nice map animation
    // which is always cool.
    this.updateBoundingBox();
  }

  private addUserLocationPin() {
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    });
    this.mapboxMap = this.mapboxMap.addControl(geolocate, 'bottom-right');
  }
  /**
   * Handle a click on the Mapbox layer of symbols.
   */
  public handleClick(feature: mapboxgl.MapboxGeoJSONFeature) {
    if (feature) {
      this.featureClicked.emit(feature);
    }

    if (this.enableDirectionsToPin && !this.pinDirectionLoaded) {
      const geometry: Point = feature.geometry as Point;
      this.pinDirectionLoaded = true;
      this.mapDirectionsService.addDirections(this.mapboxMap, {
        destination: geometry.coordinates.slice() as [number, number],
      });
    }
  }

  public getSavedMapPosition(): Observable<MapPosition> {
    return this.mapId ? this.ngrxStore.select(selectMapPosition, this.mapId).pipe(take(1)) : of(null);
  }

  public setMapAsInBeginning() {
    this.firstWheel = false;
    this.firstDrag = false;
    this.zoom = this.initZoom ? [this.initZoom] : JSON.parse(JSON.stringify(this.zoom));
    this.setMapCenter();
  }

  public onZoomEnd(event: any) {
    if (!event.originalEvent) {
      this.initZoom = event?.target?.transform._zoom;
    }
  }

  public onWheel() {
    this.firstWheel = true;
  }

  public onDragStart() {
    this.firstDrag = true;
  }

  public savePosition(event: mapboxgl.MapboxEvent) {
    if (this.mapId) {
      this.ngrxStore.dispatch(
        new SaveMapPositionAction(this.mapId, {
          bounds: event.target.getBounds(),
          center: event.target.getCenter(),
          zoom: event.target.getZoom(),
        })
      );
    }
  }

  private emitDistinctPinList(list: number[]) {
    if (JSON.stringify(list) !== JSON.stringify(this.lastPinList)) {
      this.lastPinList = list;
      this.pinList.emit(list);
    }
  }

  /**
   * Set the .center member that is passed
   * onto the mapbox MapComponent.
   */
  private setMapCenter() {
    let centerCoordinate;

    if ('features' in this.geoJSON && !this.geoJSON.features.length && !this.geo) {
      return;
    }

    if (isGeoJSONFeatureCollection(this.geoJSON)) {
      const bbox = this.getBoundingBoxFromCollection(this.geoJSON);
      centerCoordinate = bbox.getCenter();
    } else {
      centerCoordinate = this.geoJSON?.geometry?.coordinates;
    }

    // [-0.152352, 51.507298] is default coordinates (map center)
    const defaultCoords = this.geo || [-0.152352, 51.507298];
    this.center = this.disableZoom ? defaultCoords : JSON.parse(JSON.stringify(centerCoordinate));

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Set mapbox.Map.bounds property with a bounding box calculated
   * from a collection of GeoJSON.Feature.
   * Does nothing in context of single GeoJSON.Feature
   * as initial setting up the .center member is all we need.
   *
   * Note: the mapbox.Map fires an
   * animation to re-center the view when
   * the mgl-map element's "bounds" input is updated.
   */
  private updateBoundingBox(retrieveSavedPosition = true) {
    if (!isGeoJSONFeatureCollection(this.geoJSON)) {
      this.setMapCenter();
    } else {
      const json = this.geoJSON;

      if (this.mapboxMap && !this.geoJSON?.features?.length && this.geo) {
        this.mapboxMap.setCenter(new mapboxgl.LngLat(this.geo.long, this.geo.lat));
      }

      if (this.mapId && retrieveSavedPosition) {
        this.getSavedMapPosition().subscribe((position) => {
          if (position) {
            this.bounds = position.bounds;
            this.zoom = [position.zoom];
            this.mapboxMap.setCenter(position.center);
          } else {
            this.bounds = this.getBoundingBoxFromCollection(json);
          }

          this.changeDetectorRef.detectChanges();
        });
      } else {
        this.bounds = this.getBoundingBoxFromCollection(this.geoJSON);
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  /**
   * A helper to get by ID an active GeoJSON.Feature
   * from the collection of features.
   * Note: For this occasion our utility functions that create
   * GeoJSON.Feature object require "id" property on them.
   */
  private getActiveFeature(
    idToMatch: number | string
  ): GeoJSON.Feature<GeoJSON.Point, PopupGeoJsonProperties> | undefined {
    const geoJSON = this.geoJSON;

    if (isGeoJSONFeatureCollection(geoJSON) && idToMatch) {
      return geoJSON.features.filter((item) => idToMatch.toString() === item.id.toString())[0];
    } else if (!isGeoJSONFeatureCollection(geoJSON) && idToMatch && idToMatch.toString() === geoJSON.id.toString()) {
      return geoJSON;
    } else {
      return undefined;
    }
  }

  /**
   * Get a bounding box from a collection of Point geometry objects.
   */
  private getBoundingBoxFromCollection(
    featureCollection: GeoJSON.FeatureCollection<GeoJSON.Point>
  ): mapboxgl.LngLatBounds | null {
    const coordinates = featureCollection.features.map((item) => item.geometry.coordinates);

    let coords = coordinates;

    if ((!coords || coords.length === 0) && this.geo) {
      console.warn('No Feature available, fallback to geolocation');
      coords = [[this.geo.long, this.geo.lat]];
    }

    if (!coords || coords.length === 0) {
      console.warn('No coordinates available for bounding box');
      return null;
    }

    const bounds = new mapboxgl.LngLatBounds(coords[0] as [number, number], coords[0] as [number, number]);

    for (const [lng, lat] of coords) {
      bounds.extend(new mapboxgl.LngLat(lng, lat));
    }

    return bounds;
  }

  /**
   * Set svg icons on the MatIconRegistry
   * to later use them with mat-icon element.
   */
  private setSvgIcons() {
    const defaultMarkerIconUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(MapComponent.ICON_PATH);
    this.iconRegistry.addSvgIcon(MapComponent.ICON_NAME, defaultMarkerIconUrl);
  }

  /**
   * Grab SVG icon from MatIconRegistry,
   * mangle it and register with Mapbox instance.
   */
  private setUpCustomMapboxIcon(mapboxMap: mapboxgl.Map) {
    const svgIcon: Observable<SVGElement> = this.iconRegistry.getNamedSvgIcon(MapComponent.ICON_NAME);
    const svgIconDataUri: Observable<SVGElement> = svgIcon.pipe(map((svg: SVGElement) => svg));

    svgIconDataUri.pipe(take(1)).subscribe((icon: SVGElement) => {
      if (mapboxMap) {
        this.addMarkerToMap(mapboxMap, icon);
      }
    });
  }

  private createPinImages(icon: SVGElement) {
    const html = document.getElementsByTagName('html')[0];
    const defaultColor = getComputedStyle(html).getPropertyValue('--ten-map-default-pin-color');
    const activeColor = getComputedStyle(html).getPropertyValue('--ten-map-active-pin-color');

    icon.setAttribute('width', '40');
    icon.setAttribute('fill', defaultColor);
    icon.setAttribute('height', '44');
    icon.setAttribute('viewBox', '-1 -1 26 26');

    const defaultSvgString = new XMLSerializer().serializeToString(icon);
    const defaultImage = document.createElement('img');
    defaultImage.setAttribute('src', 'data:image/svg+xml;base64,' + btoa(defaultSvgString));

    // Changing only the viewbox to keep the boundaries the same width and height
    icon.setAttribute('fill', activeColor);
    icon.setAttribute('viewBox', '4 2 15 20');

    const activeImage = document.createElement('img');
    const activeSvgString = new XMLSerializer().serializeToString(icon);
    activeImage.setAttribute('src', 'data:image/svg+xml;base64,' + btoa(activeSvgString));

    return [defaultImage, activeImage];
  }

  public showPopup(feature: mapboxgl.MapboxGeoJSONFeature | Feature<Point, PopupGeoJsonProperties>) {
    const geometry: Point = feature.geometry as Point;
    const coordinates = geometry.coordinates.slice() as mapboxgl.LngLatLike;

    if (!this.hidePopupText) {
      this.popup.setLngLat(coordinates).setHTML(feature.properties.popupHTML).addTo(this.mapboxMap);
    }
  }

  public emitUniqueFeatures(event: mapboxgl.EventData) {
    // Wait for the map to be loaded to ensure all features are rendered
    timer(0, 200)
      .pipe(
        filter(() => this.mapboxMap.loaded()),
        take(1)
      )
      .subscribe(() => {
        const renderedFeatures = this.mapboxMap.queryRenderedFeatures(null, { layers: ['points'] });
        const uniqueFeatures = renderedFeatures.filter((element, index, array) => array.indexOf(element) === index);
        const pins = uniqueFeatures.map((feature) => +feature.id);

        if (this.mapId && uniqueFeatures.length) {
          this.ngrxStore.dispatch(new SaveMapPositionAction(this.mapId, { pins }));
        }

        this.emitDistinctPinList(pins);
      });
  }

  private emitMapMoved() {
    const bounds = this.mapboxMap.getBounds();
    const geolocation = this.mapboxMap.getCenter();
    const radiusKm = this.getVisibleMapRadiusKm(bounds, geolocation);

    if (this.searchOnDrag && this.mapboxMap.getSource('points')) {
      (this.mapboxMap.getSource('points') as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }

    this.popup.remove();

    // Max distance can be set to 50
    this.mapMoved.emit({ geolocation, maxDistance: Math.min(radiusKm, 50) });
  }

  private onMouseMoveOverPoints(mouseMoveEvent) {
    this.mapboxMap.getCanvas().style.cursor = 'pointer';

    if (mouseMoveEvent.features.length > 0 && isGeoJSONFeatureCollection(this.geoJSON)) {
      if (this.hoveredStateId !== mouseMoveEvent.features[0].id) {
        // If feature id equal hovered id set change marker image
        this.mapboxMap.setLayoutProperty('points', 'icon-image', [
          'match',
          ['id'],
          mouseMoveEvent.features[0].id,
          'marker-selected', //image when id is equal hovered feature id
          'marker', // default
        ]);

        this.activeFeatureId = mouseMoveEvent.features[0].id;
        this.showPopup(mouseMoveEvent.features[0]);
        this.featureHovered.emit(mouseMoveEvent.features[0]);
      }

      this.hoveredStateId = mouseMoveEvent.features[0].id;
    }
  }

  private onMouseLeavePoints() {
    this.mapboxMap.getCanvas().style.cursor = '';
  }

  private onClickPoints(clickEvent) {
    this.handleClick(clickEvent.features[0]);
  }

  private onZoomStart(zoomStartEvent) {
    if (!!zoomStartEvent && !!zoomStartEvent.originalEvent && !!zoomStartEvent.originalEvent.path?.[1]) {
      const className = zoomStartEvent.originalEvent.path[1].className;
      if (zoomStartEvent.originalEvent.type === 'click') {
        if (className === 'mapboxgl-ctrl-zoom-in') {
          this.zoomIn.emit(zoomStartEvent);
        } else if (className === 'mapboxgl-ctrl-zoom-out') {
          this.zoomOut.emit(zoomStartEvent);
        }
      }
    }
  }

  private onMoveEnd(moveEndEvent) {
    if (this.searchOnDrag) {
      this.emitMapMoved();
    }

    this.emitUniqueFeatures(moveEndEvent);
  }

  /**
   * Adds the pin marker to the map, for feature collection it adds multiple markers
   */
  private addMarkerToMap(mapboxMap: mapboxgl.Map, icon: SVGElement) {
    const geoJSON = this.geoJSON as Feature<Point, PopupGeoJsonProperties>;
    const pinImages = this.createPinImages(icon);

    pinImages[1].onload = () => {
      const source = mapboxMap ? (mapboxMap?.getSource('points') as mapboxgl.GeoJSONSource) : null;

      if (source) {
        source.setData(geoJSON);
      } else {
        mapboxMap.addSource('points', {
          type: 'geojson',
          data: geoJSON,
        });

        // Add a symbol layer from source and set layout properties
        mapboxMap.addLayer({
          id: 'points',
          type: 'symbol',
          source: 'points',
          layout: {
            'icon-image': 'marker',
            'icon-allow-overlap': this.searchOnDrag,
            'text-allow-overlap': true,
          },
        });

        // Add pin images
        mapboxMap.addImage('marker', pinImages[0]);
        mapboxMap.addImage('marker-selected', pinImages[1]);
      }

      mapboxMap.once('idle', (idleEvent) => {
        this.emitUniqueFeatures(idleEvent);
      });
    };
  }

  private getVisibleMapRadiusKm(bounds: mapboxgl.LngLatBounds, center: mapboxgl.LngLat): number {
    const north = bounds.getNorthEast().lat;
    const east = bounds.getNorthEast().lng;

    const northPoint = new mapboxgl.LngLat(center.lng, north);
    const eastPoint = new mapboxgl.LngLat(east, center.lat);

    const northDistance = center.distanceTo(northPoint);
    const eastDistance = center.distanceTo(eastPoint);

    const nearestMeters = Math.min(northDistance, eastDistance);

    return parseFloat((nearestMeters / 1000).toFixed(2));
  }
}
